import { useEffect, useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { api } from '../lib/api'

const LEVEL_THRESHOLDS = [0, 100, 250, 500, 800, 1200, 1700, 2300, 3000, 4000, 5200, 6600, 8200, 10000, 12500, 15500, 19000, 23000, 27500, 33000, 40000, 48000, 57000, 67000, 80000]

const STAGE_LABELS: Record<string, string> = {
  NOOB: '🟤 Noob',
  ROOKIE: '🟢 Rookie',
  WARRIOR: '🔵 Warrior',
  CHAMPION: '🟣 Champion',
  LEGEND: '🟡 Legend',
  MYTHIC: '🔴 Mythic',
}

export default function Dashboard() {
  const { user } = useAuth()
  const [stats, setStats] = useState<any>(null)
  const [avatar, setAvatar] = useState<any>(null)
  const [recentWorkouts, setRecentWorkouts] = useState<any[]>([])

  useEffect(() => {
    api.getStats().then(({ stats, avatar }) => { setStats(stats); setAvatar(avatar) }).catch(() => {})
    api.getWorkoutHistory(5).then(({ logs }) => setRecentWorkouts(logs)).catch(() => {})
  }, [])

  const currentXP = stats?.xp || user?.xp || 0
  const currentLevel = stats?.level || user?.level || 1
  const nextLevelXP = LEVEL_THRESHOLDS[currentLevel] || LEVEL_THRESHOLDS[LEVEL_THRESHOLDS.length - 1]
  const prevLevelXP = LEVEL_THRESHOLDS[currentLevel - 1] || 0
  const xpInLevel = currentXP - prevLevelXP
  const xpNeeded = nextLevelXP - prevLevelXP
  const xpPercent = xpNeeded > 0 ? Math.min(100, (xpInLevel / xpNeeded) * 100) : 100

  return (
    <div>
      <div className="page-header">
        <h2>HQ — Command Center</h2>
        <p>Welcome back, {user?.displayName || user?.firstName || 'Player'}!</p>
      </div>

      {/* Stats Overview */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{currentLevel}</div>
          <div className="stat-label">Level</div>
          {avatar && (
            <div style={{ marginTop: '0.5rem' }}>
              <span className={`avatar-stage stage-${(avatar.stage || 'noob').toLowerCase()}`}>
                {STAGE_LABELS[avatar?.stage] || 'Noob'}
              </span>
            </div>
          )}
        </div>
        <div className="stat-card">
          <div className="stat-value">{currentXP.toLocaleString()}</div>
          <div className="stat-label">Total XP</div>
        </div>
        <div className="stat-card">
          <div className="streak-display" style={{ justifyContent: 'center' }}>
            <span className="streak-flame">{(stats?.streak || 0) >= 7 ? '🔥' : '⚡'}</span>
            <div className="stat-value">{stats?.streak || 0}</div>
          </div>
          <div className="stat-label">Day Streak</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{recentWorkouts.length > 0 ? recentWorkouts.length : '—'}</div>
          <div className="stat-label">Recent Workouts</div>
        </div>
      </div>

      {/* XP Progress Bar */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Level Progress</span>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
            {xpInLevel} / {xpNeeded} XP to Level {currentLevel + 1}
          </span>
        </div>
        <div className="xp-bar-container">
          <div className="xp-bar-fill" style={{ width: `${xpPercent}%` }} />
          <span className="xp-bar-text">{Math.round(xpPercent)}%</span>
        </div>
      </div>

      {/* Recent Workouts */}
      <div className="card" style={{ marginTop: '1rem' }}>
        <div className="card-header">
          <span className="card-title">Recent Activity</span>
        </div>
        {recentWorkouts.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '2rem 0' }}>
            No workouts yet. Start your first quest! 💪
          </p>
        ) : (
          recentWorkouts.map(log => (
            <div key={log.id} className="quest-item">
              <span className="quest-icon">🏋️</span>
              <div className="quest-info">
                <div className="quest-name">{log.durationMin ? `${log.durationMin} min workout` : 'Workout'}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                  {new Date(log.date).toLocaleDateString()} · {log.entries?.length || 0} exercises
                </div>
              </div>
              <span className="quest-xp">+{log.xpEarned} XP</span>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
